import {
  DESIGN_ENUMS,
  DESIGN_INLINE,
  DESIGN_INLINE_VERTICAL,
  DESIGN_STANDARD,
  DESIGN_VERTICAL,
  SIZE_ENUMS,
  SIZE_STANDARD,
} from '../../ChannelList/constants/carousels';
import { INDENT_FOR_LW_IN_POPUP } from '../constants';
import { TYPE_DEFAULT, TYPE_ENUMS } from '../constants/channel';
import { DEEP_SEARCH_REDIRECT_ENUMS } from '../constants/deepSearch';
import { ENVIRONMENT_ENUMS } from '../constants/environment';
import { GEN_AI_DEFAULT_VIDEO_COUNT, GEN_AI_MAX_VIDEO_COUNT, GEN_AI_MIN_VIDEO_COUNT } from '../constants/genAi';
import { AVAILABLE_CHIP_TAGS, AVAILABLE_DEEP_SEARCH_TAGS } from '../constants/label';
import { LAYOUT_TYPE_ENUMS, LAYOUT_TYPE_IN_POPUP } from '../constants/lw';
import { PAGE_CHANNEL, PAGE_MAIN, PAGE_PLAYER, PAGE_SEARCH } from '../constants/pages';
import { FILTER_CONFIG_LANGUAGE_DEFAULT, MINIMUM_LANGUAGE_COUNT } from '../constants/queryParams';
import {
  CHANNEL_LIST_PATH,
  CHANNEL_VIEW_PATH,
  DEFAULT_VIEW_PATH,
  SEARCH_AI_VIEW_PATH,
  SEARCH_PATH,
  SEARCH_VIEW_PATH,
  SHARE_VIEW_PATH,
  SINGLE_VIEW_PATH,
} from '../constants/routePath';
import { ALL_SOCIAL_TYPES, SHARES, VIDEO_ENGAGEMENT_SOCIAL_TYPE } from '../constants/socialStatistic';
import { TAB_ENUMS } from '../constants/tabs';
import { APPEARANCE_ENUMS } from '@/mui/constants';

import { initialState as initial } from '../redux/slices/common';
import { castToOldChannelFields, castToOldWatchFields } from '@/modules/common/helpers/backwardCompatible';

import { getChannelArray, getOverrideConfig, isInternalType } from './embedCodeParams';
import { isInRange } from './number';
import { replaceDomain } from './url';
import { isMobileUserAgent } from './userAgent';

export const toValidEnum = (enumValue, enumsList, defaultEnum) =>
  enumsList.find((enum$) => enum$ === enumValue) ?? defaultEnum;

const toInt = (num, defaultValue) => {
  const parsed = parseInt(num, 10);

  return Number.isNaN(parsed) ? defaultValue : parsed;
};

const PAGE_TO_ROUTE_MAPPER = {
  [PAGE_MAIN]: [CHANNEL_LIST_PATH],
  [PAGE_CHANNEL]: [CHANNEL_VIEW_PATH],
  [PAGE_PLAYER]: [DEFAULT_VIEW_PATH, SHARE_VIEW_PATH, SINGLE_VIEW_PATH, SEARCH_AI_VIEW_PATH],
  [PAGE_SEARCH]: [SEARCH_PATH, SEARCH_VIEW_PATH],
};

const getWatchModel = (conf) => {
  const search = conf.search ?? initial.search;
  const deepSearch = conf.deepSearch ?? initial.deepSearch;
  const emailShare = conf.emailShare ?? initial.emailShare;

  const verticalTypeContains = conf.channels.some((channel) =>
    [DESIGN_VERTICAL, DESIGN_INLINE_VERTICAL].includes(channel.design),
  );

  const layout = !verticalTypeContains
    ? toValidEnum(conf.layout, LAYOUT_TYPE_ENUMS, initial.layout)
    : LAYOUT_TYPE_IN_POPUP;
  const hubOffsetDelta = layout === LAYOUT_TYPE_IN_POPUP && isMobileUserAgent ? INDENT_FOR_LW_IN_POPUP : 0;

  const parsedGenAiVideoCount = toInt(conf.genAICount, GEN_AI_DEFAULT_VIDEO_COUNT);

  const dsTags = conf.dsTags.filter((tag) => AVAILABLE_DEEP_SEARCH_TAGS.includes(tag));
  const delayPreview = parseInt(conf.delayPreview, 10) || initial.delayPreview;
  const tags = conf.tags ?? initial.tags;

  return {
    appearance: toValidEnum(conf.appearance, APPEARANCE_ENUMS, initial.appearance),
    autoplay: conf.autoplay ?? initial.autoplay,
    autoplayMobile: conf.autoplayMobile ?? initial.autoplayMobile,
    autoplayNext: conf.autoplayNext ?? initial.autoplayNext,
    backMobile: conf.backMobile ?? initial.backMobile,
    basePath: conf.basePath.replace('https://', ''),
    bg: conf.bg ?? initial.bg,
    cDescColor: conf.cDescColor ?? initial.cDescColor,
    cDescSize: conf.cDescSize ?? initial.cDescSize,
    cInfoColor: conf.cInfoColor ?? initial.cInfoColor,
    cTitleColor: conf.cTitleColor ?? initial.cTitleColor,
    cTitleSize: conf.cTitleSize ?? initial.cTitleSize,
    cVidTitleColor: conf.cVidTitleColor ?? initial.cVidTitleColor,
    cVidTitleSize: conf.cVidTitleSize ?? initial.cVidTitleSize,
    chapters: conf.chapters ?? initial.chapters,
    color: conf.color ?? initial.color,
    comments: conf.comments ?? initial.comments,
    css: conf.css ?? initial.css,
    dblClickFlow: conf.dblClickFlow ?? initial.dblClickFlow,
    deepSearch: deepSearch && !!dsTags.length,
    delayPreview,
    description: conf.description ?? initial.description,
    dockMobile: conf.dockMobile ?? initial.dockMobile,
    download: conf.download ?? initial.download,
    dsRedirect: toValidEnum(deepSearch && conf.dsRedirect, DEEP_SEARCH_REDIRECT_ENUMS, initial.dsRedirect),
    dsTags,
    emailShare,
    engagement: ALL_SOCIAL_TYPES.reduce((acc, feature) => {
      acc[feature] =
        conf.engagement.some((key) => VIDEO_ENGAGEMENT_SOCIAL_TYPE[key] === feature) &&
        (emailShare || (!emailShare && feature !== SHARES));
      return acc;
    }, {}),
    environment: toValidEnum(conf.environment, ENVIRONMENT_ENUMS, initial.environment),
    excludedTags: (tags && conf.excludedTags) || [],
    fixedTags: tags && conf.fixedTags ? conf.fixedTags : [],
    // empty string is device dependent that why used || and not ?? operator
    font: conf.font || initial.font,
    fontSize: conf.fontSize ?? initial.fontSize,
    genAI: search && (conf.genAI ?? initial.genAI),
    genAICount: isInRange(parsedGenAiVideoCount, GEN_AI_MIN_VIDEO_COUNT, GEN_AI_MAX_VIDEO_COUNT)
      ? parsedGenAiVideoCount
      : GEN_AI_DEFAULT_VIDEO_COUNT,
    highlights: conf.highlights ?? initial.highlights,
    id: conf.id,
    inlinePlayer: { name: conf.inlinePlayer?.name ?? initial.inlinePlayer.name },
    inlineVerticalPlayer: { name: conf.inlineVerticalPlayer?.name ?? initial.inlineVerticalPlayer.name },
    js: conf.js ?? initial.js,
    languages:
      conf.languages?.length >= MINIMUM_LANGUAGE_COUNT
        ? [{ ...FILTER_CONFIG_LANGUAGE_DEFAULT }, ...conf.languages]
        : (conf.languages ?? []),
    layout,
    offsetBottom: toInt(conf.offsetBottom, initial.offsetBottom),
    offsetTop: toInt(conf.offsetTop, initial.offsetTop) + hubOffsetDelta,
    player: { name: conf.player?.name ?? initial.player.name },
    popupSize: toInt(conf.popupSize, initial.popupSize),
    publisher: { slug: conf.publisher?.slug },
    sTitleColor: conf.sTitleColor ?? initial.sTitleColor,
    sTitleSize: conf.sTitleSize ?? initial.sTitleSize,
    sVidInfoColor: conf.sVidInfoColor ?? initial.sVidInfoColor,
    sVidInfoSize: conf.sVidInfoSize ?? initial.sVidInfoSize,
    sVidTitleColor: conf.sVidTitleColor ?? initial.sVidTitleColor,
    sVidTitleSize: conf.sVidTitleSize ?? initial.sVidTitleSize,
    search,
    searchLabel: conf.searchLabel ?? initial.searchLabel,
    searchPlaceholder: conf.searchPlaceholder ?? initial.searchPlaceholder,
    shareService: conf.shareService ?? initial.shareService,
    slides: conf.slides ?? initial.slides,
    socialShare: conf.socialShare ?? initial.socialShare,
    sound: conf.sound ?? initial.sound,
    soundMobile: conf.soundMobile ?? initial.soundMobile,
    status: conf.status ?? initial.status,
    tabOrder: conf.tabOrder ?? TAB_ENUMS,
    tagCount: tags ? toInt(conf.tagCount, initial.tagCount) : 0,
    tagMax: tags ? toInt(conf.tagMax, initial.tagMax) : 0,
    tagMin: tags ? toInt(conf.tagMin, initial.tagMin) : 0,
    tagPages:
      tags && conf.tagPages
        ? conf.tagPages.reduce((acc, currValue) => acc.concat(PAGE_TO_ROUTE_MAPPER[currValue] ?? []), [])
        : [],
    tagTypes: tags && conf.tagTypes ? conf.tagTypes.filter((tag) => AVAILABLE_CHIP_TAGS.includes(tag)) : [],
    tags,
    title: conf.title ?? initial.title,
    transcriptDownload: conf.transcriptDownload ?? initial.transcriptDownload,
    verticalPlayer: { name: conf.verticalPlayer?.name ?? initial.verticalPlayer.name },
    videoPreview: conf.videoPreview ?? initial.videoPreview,
    widgetOrigin: conf.widgetOrigin?.replace(/\s|((http|https):)|\//g, '') || initial.widgetOrigin,
    widgetPath: conf.widgetPath ?? initial.widgetPath,
    ...(isInternalType
      ? {
          appearance: initial.appearance,
          bg: initial.bg,
          cDescColor: initial.cDescColor,
          cDescSize: initial.cDescSize,
          cInfoColor: initial.cInfoColor,
          cTitleColor: initial.cTitleColor,
          cTitleSize: initial.cTitleSize,
          cVidTitleColor: initial.cVidTitleColor,
          cVidTitleSize: initial.cVidTitleSize,
          color: initial.color,
          css: initial.css,
          font: initial.font,
          fontSize: initial.fontSize,
          js: initial.js,
          offsetBottom: initial.offsetBottom,
          offsetTop: initial.offsetTop,
          sTitleColor: initial.sTitleColor,
          sTitleSize: initial.sTitleSize,
          sVidInfoColor: initial.sVidInfoColor,
          sVidInfoSize: initial.sVidInfoSize,
          sVidTitleColor: initial.sVidTitleColor,
          sVidTitleSize: initial.sVidTitleSize,
        }
      : {}),
  };
};

const getChannelModel = (chl) => ({
  bg: chl.bg ?? '',
  bgLogo: chl.bgLogo ?? '',
  cTitleColor: chl.cTitleColor ?? '',
  cTitleSize: chl.cTitleSize ?? '',
  cVidInfoColor: chl.cVidInfoColor ?? '',
  cVidInfoSize: chl.cVidInfoSize ?? '',
  cVidTitleColor: chl.cVidTitleColor ?? '',
  cVidTitleSize: chl.cVidTitleSize ?? '',
  descColor: chl.descColor ?? '',
  description: chl.description ?? '',
  design: toValidEnum(chl.design, DESIGN_ENUMS, DESIGN_STANDARD),
  format: toValidEnum(chl.format, SIZE_ENUMS, SIZE_STANDARD),
  id: chl.id ?? -1,
  logo: chl.logo ? replaceDomain(chl.logo) : '',
  name: chl.name,
  playlistId: chl.playlistId ?? -1,
  title: chl.title ?? '',
  titleColor: chl.titleColor ?? '',
  type: toValidEnum(chl.type, TYPE_ENUMS, TYPE_DEFAULT),
  vidInfoColor: chl.vidInfoColor ?? '',
  vidTitleColor: chl.vidTitleColor ?? '',
  ...(isInternalType
    ? {
        bg: '',
        bgLogo: '',
        cTitleColor: '',
        cTitleSize: '',
        cVidInfoColor: '',
        cVidInfoSize: '',
        cVidTitleColor: '',
        cVidTitleSize: '',
        descColor: '',
        titleColor: '',
        vidInfoColor: '',
        vidTitleColor: '',
      }
    : {}),
});

export const getFormattedConfig = (config) => {
  const channelsToView = getChannelArray();

  const overrideConfig = castToOldWatchFields(getOverrideConfig() || {});

  const channelsOverride = (overrideConfig?.channels || []).map((chl) => castToOldChannelFields(chl));

  const watchModel = getWatchModel({ ...config, ...overrideConfig });

  let replaceDuplicatedInline = false;
  let replaceDuplicatedInlineVertical = false;

  return {
    ...watchModel,
    channels: config.channels
      .filter((channel) => (Array.isArray(channelsToView) ? channelsToView.includes(channel.id) : true))
      .map((chl) => {
        const overrideChannel = channelsOverride.find((channel) => channel.id === chl.id) || {};

        const channel = getChannelModel({ ...chl, ...overrideChannel });

        if (channel.design === DESIGN_INLINE) {
          if (replaceDuplicatedInline || isMobileUserAgent) {
            channel.design = DESIGN_STANDARD;
          }

          replaceDuplicatedInline = true;
        }

        if (channel.design === DESIGN_INLINE_VERTICAL) {
          if (replaceDuplicatedInlineVertical) {
            channel.design = DESIGN_STANDARD;
          }

          replaceDuplicatedInlineVertical = true;
        }

        return {
          ...channel,

          isDisabled: false,
          playlistLoaded: false,
          playlist: [],
        };
      }),
  };
};
